import { Show } from 'solid-js';
import { DropdownOption, FieldType, FormFieldContext } from '../forms-types';
import { FieldErrorMessage, FormSelect, FormFieldLabel, FormSelectWrapper, IconWrapper } from './primitives';
import { ChevronDownCviIcon } from '../../icons-library/chevron-down-cvi';
import { countries, atosGlobalCountrySlugToCountryCode } from '../../../tools/countries-data';
import { parseFieldOptions } from '../utils';
import { getUserTypes } from '../constants';

type FieldProps = {
    field: FieldType;
    ctx: FormFieldContext;
};

// Custom WP dropdown: options come from extraSettings (newline-separated, optional pipe-separated label/value).
export const Dropdown = (props: FieldProps) => {
    const { formValues, errors, onDropdownChange } = props.ctx;

    const extraSettings = () => props.field.extraSettings;
    const required = () => props.field.required;
    const eloquaName = () => extraSettings()?.name.value ?? '';
    // Backwards compability for when the dropdown options were coming from info.value (different WP setup).
    const optionsString = () => extraSettings()?.options?.value || extraSettings()?.info.value.split(',').join('\n') || '';
    const formatedOptions = (): DropdownOption[] => parseFieldOptions(optionsString());
    const placeholderValue = () => extraSettings()?.placeholder.value ?? '';
    const placeholderText = () => `${placeholderValue()}${required() ? '*' : ''}`;

    return (
        <Show when={extraSettings()}>
            <div>
                <FormFieldLabel for={`custom-dropdown-select-${eloquaName()}`} id={`custom-dropdown-select-label-${eloquaName()}`}>
                    {placeholderText()}
                </FormFieldLabel>
                <FormSelectWrapper>
                    <FormSelect
                        id={`custom-dropdown-select-${eloquaName()}`}
                        required={required()}
                        value={formValues()[eloquaName()]}
                        hasErrors={!!errors()[eloquaName()]}
                        onChange={(e) => onDropdownChange(eloquaName(), e.currentTarget.value)}
                        name={'custom-dropdown-' + eloquaName()}
                        aria-labelledby={`custom-dropdown-select-label-${eloquaName()}`}
                        aria-describedby={`desc-${eloquaName()}`}
                        title={placeholderText()}
                    >
                        <option value="">{placeholderText().replace('*', '')}</option>
                        {formatedOptions().map((option) => (
                            <option value={option.value}>{option.text}</option>
                        ))}
                    </FormSelect>

                    <IconWrapper>
                        <ChevronDownCviIcon size="default" ariaHidden={true} />
                    </IconWrapper>
                </FormSelectWrapper>

                <span id={`desc-${eloquaName()}`} style={{ display: 'none' }}>
                    {placeholderValue()}
                </span>

                <Show when={errors()[eloquaName()]}>
                    <FieldErrorMessage errors={errors} fieldKey={eloquaName()} />
                </Show>
            </div>
        </Show>
    );
};

export const CountrySelect = (props: FieldProps) => {
    const { formValues, errors, onInputChange, localize, siteInfo } = props.ctx;

    const slug = () => props.field.slug;
    const title = () => `${localize('country', 'Country')}${props.field.required ? '*' : ''}`;
    const options = (): DropdownOption[] => countries.map((country) => ({ value: country.code, text: country.name }));
    const currentSelectCountryCode = () => atosGlobalCountrySlugToCountryCode[siteInfo.siteSlug as keyof typeof atosGlobalCountrySlugToCountryCode];

    return (
        <div>
            <FormFieldLabel for="country-select" id="country-select-label">
                {title()}
            </FormFieldLabel>

            <FormSelectWrapper>
                <FormSelect
                    id="country-select"
                    name={slug()}
                    required={props.field.required}
                    value={formValues().country?.code}
                    hasErrors={!!errors()[slug()]}
                    onChange={(e) => onInputChange(slug(), e.currentTarget.value)}
                    aria-labelledby="country-select-label"
                    aria-describedby={`desc-${slug()}`}
                    title={title()}
                    autocomplete="country"
                >
                    <option value="">{title().replace('*', '')}</option>
                    {options().map((option) => (
                        <option selected={option.value === currentSelectCountryCode()} value={option.value}>{option.text}</option>
                    ))}
                </FormSelect>

                <IconWrapper>
                    <ChevronDownCviIcon size="default" ariaHidden={true} />
                </IconWrapper>
            </FormSelectWrapper>

            <span id={`desc-${slug()}`} style={{ display: 'none' }}>
                {title()}
            </span>

            <Show when={errors()[slug()]}>
                <FieldErrorMessage errors={errors} fieldKey={slug()} />
            </Show>
        </div>
    );
};

export const UserTypeSelect = (props: FieldProps) => {
    const { formValues, errors, onInputChange, localize } = props.ctx;

    const slug = () => props.field.slug;
    const options = () => getUserTypes(localize);
    const placeholder = () => `${localize('user-type', 'User type')}${props.field.required ? '*' : ''}`;

    return (
        <div>
            <FormFieldLabel for="user-type-select" id="user-type-select-label">
                {placeholder()}
            </FormFieldLabel>

            <FormSelectWrapper>
                <FormSelect
                    id="user-type-select"
                    name={slug()}
                    required={props.field.required}
                    value={formValues()[slug()]}
                    hasErrors={!!errors()[slug()]}
                    onChange={(e) => onInputChange(slug(), e.currentTarget.value)}
                    aria-labelledby="user-type-select-label"
                    aria-describedby={`desc-${slug()}`}
                    title={placeholder()}
                >
                    <option value="">{placeholder().replace('*', '')}</option>
                    {options().map((option) => (
                        <option value={option.value}>{option.text}</option>
                    ))}
                </FormSelect>

                <IconWrapper>
                    <ChevronDownCviIcon size="default" ariaHidden={true} />
                </IconWrapper>
            </FormSelectWrapper>

            <span id={`desc-${slug()}`} style={{ display: 'none' }}>
                {placeholder()}
            </span>

            <Show when={errors()[slug()]}>
                <FieldErrorMessage errors={errors} fieldKey={slug()} />
            </Show>
        </div>
    );
};

export const CommunicationPreferenceSelect = (props: FieldProps) => {
    const { formValues, errors, onInputChange, localize } = props.ctx;

    const slug = () => props.field.slug;
    const placeholder = () => `${localize('communication-preference', 'Communication preference')}${props.field.required ? '*' : ''}`;

    return (
        <div>
            <FormFieldLabel for="communication-preference-select" id="communication-preference-select-label">
                {placeholder()}
            </FormFieldLabel>

            <FormSelectWrapper>
                <FormSelect
                    id="communication-preference-select"
                    name={slug()}
                    required={props.field.required}
                    value={formValues()[slug()]}
                    hasErrors={!!errors()[slug()]}
                    onChange={(e) => onInputChange(slug(), e.currentTarget.value)}
                    aria-labelledby="communication-preference-select-label"
                    aria-describedby={`desc-${slug()}`}
                    title={placeholder()}
                >
                    <option value="">{placeholder().replace('*', '')}</option>
                    <option value="Phone">{localize('phone', 'Phone')}</option>
                    <option value="Email">{localize('email', 'E-mail')}</option>
                </FormSelect>

                <IconWrapper>
                    <ChevronDownCviIcon size="default" ariaHidden={true} />
                </IconWrapper>
            </FormSelectWrapper>

            <span id={`desc-${slug()}`} style={{ display: 'none' }}>
                {placeholder()}
            </span>

            <Show when={errors()[slug()]}>
                <FieldErrorMessage errors={errors} fieldKey={slug()} />
            </Show>
        </div>
    );
};
